import { useMemo } from 'react';
import { Briefcase, Search, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { MultiSelectFilter } from '../../components/filters/MultiSelectFilter';

const toOptions = (values: string[]) => values.map((value) => ({ value, label: value }));

interface PipelineFilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  jobTitleOptions: string[];
  selectedJobTitles: string[];
  onJobTitlesChange: (value: string[]) => void;
  teamOptions: string[];
  selectedTeams: string[];
  onTeamsChange: (value: string[]) => void;
  onReset: () => void;
  visibleCount: number;
  totalCount: number;
}

export default function PipelineFilterBar({
  search,
  onSearchChange,
  jobTitleOptions,
  selectedJobTitles,
  onJobTitlesChange,
  teamOptions,
  selectedTeams,
  onTeamsChange,
  onReset,
  visibleCount,
  totalCount,
}: PipelineFilterBarProps) {
  const isFilterActive = useMemo(
    () => search.trim().length > 0 || selectedJobTitles.length > 0 || selectedTeams.length > 0,
    [search, selectedJobTitles, selectedTeams],
  );

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-border-subtle bg-card p-4">
      <div className="flex flex-1 flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle" size={16} />
          <Input
            type="text"
            placeholder="Search candidate name, email..."
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            className="w-full max-w-xs bg-bg-main pl-9"
          />
        </div>

        <MultiSelectFilter
          icon={<Briefcase size={15} className="shrink-0 text-text-subtle" />}
          label="Job title"
          allLabel="All Job Titles"
          searchPlaceholder="Search job title..."
          emptyText="No job title found."
          options={toOptions(jobTitleOptions)}
          selected={selectedJobTitles}
          onChange={onJobTitlesChange}
        />

        <MultiSelectFilter
          icon={<Users size={15} className="shrink-0 text-text-subtle" />}
          label="Team"
          allLabel="All Teams"
          searchPlaceholder="Search team..."
          emptyText="No team found."
          options={toOptions(teamOptions)}
          selected={selectedTeams}
          onChange={onTeamsChange}
        />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={!isFilterActive}
          onClick={onReset}
        >
          Reset
        </Button>
      </div>

      <div className="shrink-0 text-sm text-text-subtle">
        Showing <span className="font-semibold text-text-main">{visibleCount}</span> /{' '}
        <span className="font-semibold text-text-main">{totalCount}</span> candidates
      </div>
    </div>
  );
}
